/**
 * Utilities for highlighting search terms in markdown content
 */

/**
 * Escape special regex characters in a string
 * @param {string} str - String to escape
 * @returns {string} - Escaped string safe for use in regex
 */
const escapeRegex = str => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Highlight search query in markdown content
 * Wraps matches with special markers that can be styled
 *
 * @param {string} content - Raw markdown content
 * @param {string} query - Search query to highlight
 * @returns {string} - Content with highlight markers
 */
export const highlightSearchTerms = (content, query) => {
  if (!content || !query || query.trim() === '') {
    return content
  }

  const trimmedQuery = query.trim()
  const escapedQuery = escapeRegex(trimmedQuery)

  // Use a case-insensitive global regex to find all matches
  const regex = new RegExp(`(${escapedQuery})`, 'gi')

  // Replace matches with marked text
  // We use a unique marker that won't conflict with markdown syntax
  return content.replace(regex, '==HIGHLIGHT_START==$1==HIGHLIGHT_END==')
}

/**
 * Process highlighted markdown for rendering
 * Converts highlight markers to HTML-safe format for react-markdown
 *
 * @param {string} content - Content with highlight markers
 * @returns {string} - Content ready for markdown rendering
 */
export const prepareHighlightedContent = content => {
  if (!content) return content

  // Replace our markers with markdown that will be preserved
  // We'll use inline code-style backticks with a special class marker
  return content.replace(/==HIGHLIGHT_START==/g, '<mark>').replace(/==HIGHLIGHT_END==/g, '</mark>')
}

/**
 * Combined function to highlight and prepare content
 * @param {string} content - Raw markdown content
 * @param {string} query - Search query to highlight
 * @returns {string} - Processed content ready for rendering
 */
export const processContentWithHighlights = (content, query) => {
  const highlighted = highlightSearchTerms(content, query)
  return prepareHighlightedContent(highlighted)
}
